import { cn } from "@app/lib/utils";
import { URLS } from "@app/shared";
import { Button, Link } from "@design-system/components";
import { AnimateFrom } from "@design-system/components/animations/AnimateFrom";
import { useInViewport } from "@design-system/hooks/use-view-port";
import { trans } from "@mongez/localization";
import { ChevronRight } from "lucide-react";
import AboutUsVideo from "./AboutUsVideo";

export function WhoWeAreSection() {
  const { isInView, ref } = useInViewport({ rootMargin: "-100px" });

  return (
    <section className="overflow-hidden bg-dark-blue" ref={ref}>
      <div className="flex flex-col items-center bg-dark-blue lg:flex-row">
        {/* Content Section */}
        <div className="flex w-full items-center">
          <div className="container">
            <AnimateFrom
              isInView={isInView}
              direction="left"
              className="w-full px-4 py-8 max-lg:pb-0 sm:py-12 md:py-14 lg:py-16"
            >
              <div
                className={cn(
                  "text-clamp-title font-bold uppercase text-bright-orange transition-all lg:translate-y-[40px]",
                  isInView &&
                    "[--slide-distance:40px] lg:animate-[slideUp_0.5s_ease-out_0.9s_forwards]",
                )}
                style={{ fontSize: "clamp(1rem, 4vw, 1.5rem)" }}
              >
                {trans("aboutSenaat")}
              </div>

              <div className="w-full">
                <div
                  className={cn(
                    "mb-4 mt-3 font-bold text-white sm:mb-5 sm:mt-4 md:mb-6 lg:max-w-0 lg:overflow-hidden",
                    isInView
                      ? "lg:animate-[expandWidth_1.5s_ease-out_1s_forwards]"
                      : "",
                  )}
                >
                  <h3 className="w-full text-clamp-heading font-medium leading-[77px] max-lg:leading-[42px] lg:min-w-max">
                    {trans("shapingIndustries")}{" "}
                    <br className="max-lg:hidden" />
                    {trans("empoweringTomorrow")}
                  </h3>
                </div>
              </div>

              <div className="w-full overflow-hidden">
                <div
                  className={cn(
                    "mb-6 text-white/80 sm:mb-7 md:mb-8 lg:max-w-0 lg:overflow-hidden",
                    isInView &&
                      "lg:animate-[expandWidth_1.5s_ease-out_1.2s_forwards]",
                  )}
                >
                  <p className="text-clamp-body font-light leading-clamp-body lg:min-w-max lg:overflow-hidden">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: trans("companyDescriptionLine1"),
                      }}
                    />
                  </p>
                </div>
              </div>

              <Link to={URLS.aboutUs.root}>
                <Button
                  variant="outline"
                  size="lg"
                  className={cn(
                    "flex w-max items-center gap-2 rounded-full px-4 py-2 text-clamp-button font-normal uppercase text-white hover:bg-transparent md:px-6 md:py-3 lg:translate-y-[-40px] lg:px-10 lg:py-4",
                    "max-lg:px-3 max-lg:py-1", // Much smaller paddings for small laptop screens
                    isInView &&
                      "[--slide-distance:-40px] lg:animate-[slideDown_0.5s_ease-out_0.9s_forwards]",
                  )}
                  rightIcon={
                    <ChevronRight size={26} className="rtl:rotate-180" />
                  }
                >
                  <p className="h-6">{trans("exploreMore")}</p>
                </Button>
              </Link>
            </AnimateFrom>
          </div>
        </div>

        {/* Image Section - extends to page edge on desktop */}
        <div
          className={cn(
            "relative mt-6 w-auto lg:mt-0 lg:w-full",
            isInView ? "animate-from-right rtl:animate-from-left" : "opacity-0",
          )}
        >
          <AboutUsVideo />
        </div>
      </div>
    </section>
  );
}
